import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getAllAccommodationsByProject } from '../../services/api/implementation/impl';
import Button from '../../../shared/components/ui/Button';
import { useParams } from 'react-router-dom';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';

export const CreateExtraCharges = ({
  projectName,
  createSuccess,
  onClose,
  existingPricingType,
  measurementTypeWithoutSlash
}) => {
  const userId = Cookies.get('userId');
  const { id: projectId } = useParams();
  const [selectedPricingType, setSelectedPricingType] = useState('');
  const [selectedAccommodationType, setSelectedAccommodationType] = useState('GROUP');
  const [accommodations, setAccommodations] = useState([]);
  const [step, setStep] = useState(existingPricingType ? 2 : 1);
  const [openDropdowns, setOpenDropdowns] = useState({});
  const [removingIds, setRemovingIds] = useState({});
  const [extraCharges, setExtraCharges] = useState([
    {
      name: '',
      description: '',
      price: '',
      accommodation_ids: [],
      mode: existingPricingType || '',
      accomodation_type: 'GROUP'
    }
  ]);

  const { data: accommodationData } = useQuery({
    queryKey: ['accommodations', projectId],
    queryFn: () =>
      // getAllAccommodationsByProject(projectId, `&accomodationType=${selectedAccommodationType}`),
      getAllAccommodationsByProject(projectId),

    enabled: !!projectId
  });

  // Track page view
  useEffect(() => {
    mixpanel.track('Create Extra Charges Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    if (accommodationData) {
      const accommodationOptions = accommodationData?.map((acc) => ({
        value: acc.id.toString(),
        label: acc.name
      }));
      setAccommodations(accommodationOptions);
    }
  }, [accommodationData]);

  useEffect(() => {
    if (existingPricingType) {
      setSelectedPricingType(existingPricingType);
      setExtraCharges((prev) => prev.map((charge) => ({ ...charge, mode: existingPricingType })));
    }
  }, [existingPricingType]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const dropdowns = document.querySelectorAll('[data-dropdown]');
      let clickedInside = false;

      dropdowns.forEach((dropdown) => {
        if (dropdown.contains(event.target)) {
          clickedInside = true;
        }
      });

      if (!clickedInside) {
        setOpenDropdowns({});
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handlePricingTypeSelect = (type) => {
    setSelectedPricingType(type);
    setExtraCharges((prev) => prev.map((charge) => ({ ...charge, mode: type })));
    setStep(2);
  };

  // const handleAccommodationTypeChange = (e) => {
  //   const newType = e.target.value;
  //   setSelectedAccommodationType(newType);
  //   setExtraCharges((prev) => prev.map((charge) => ({ ...charge, accomodation_type: newType })));
  // };

  const handleInputChange = (e, index, field) => {
    const { value } = e.target;
    const updatedExtraCharges = [...extraCharges];
    updatedExtraCharges[index][field] = value;
    setExtraCharges(updatedExtraCharges);
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const body = { extra_charges: extraCharges };

    createSuccess(body);
  };

  const getTypeTitle = () => {
    switch (selectedPricingType) {
      case 'accommodation':
        return 'Accommodation Based Extra Charge';
      case 'sqft':
        return 'sqft Based Extra Charge';
      case 'sqmt':
        return 'sqmt Based Extra Charge';
      default:
        return '';
    }
  };

  const addExtraCharge = () => {
    setExtraCharges([
      ...extraCharges,
      {
        name: '',
        description: '',
        price: '',
        accommodation_ids: [],
        mode: selectedPricingType
        // accomodation_type: selectedAccommodationType
      }
    ]);
  };

  const removeExtraCharge = (index) => {
    setExtraCharges((prevExtraCharges) => prevExtraCharges.filter((_, i) => i !== index));
  };

  const toggleDropdown = (index) => {
    setOpenDropdowns((prev) => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const handleAccommodationSelection = (index, accommodationId) => {
    console.log('Accommodation selection:', {
      index,
      accommodationId,
      currentIds: extraCharges[index].accommodation_ids
    });
    const updatedExtraCharges = [...extraCharges];
    const currentIds = updatedExtraCharges[index].accommodation_ids;

    if (currentIds.includes(accommodationId)) {
      // Removing accommodation
      setRemovingIds((prev) => ({ ...prev, [index]: accommodationId }));
      setTimeout(() => {
        updatedExtraCharges[index].accommodation_ids = currentIds.filter(
          (id) => id !== accommodationId
        );
        console.log(
          'Removed accommodation, new list:',
          updatedExtraCharges[index].accommodation_ids
        );
        setExtraCharges(updatedExtraCharges);
        setRemovingIds((prev) => ({ ...prev, [index]: null }));
      }, 150); // Small delay for visual feedback
    } else {
      // Adding accommodation
      updatedExtraCharges[index].accommodation_ids = [...currentIds, accommodationId];
      console.log('Added accommodation, new list:', updatedExtraCharges[index].accommodation_ids);
      setExtraCharges(updatedExtraCharges);
    }
  };

  const getStatusStyle = (status) => {
    if (
      [
        'unpaid',
        'Unpaid',
        'unapproved',
        'Unapproved',
        'inactive',
        'Inactive',
        'INACTIVE',
        'Denied',
        'Rejected',
        'Low',
        false,
        'No',
        'no',
        'Delivery Pending',
        'Payment Returned'
      ].includes(status)
    ) {
      return 'bg-[#C2281B]';
    } else if (['Medium', 'Confirm Delivery'].includes(status)) {
      return 'bg-orange-400';
    } else if (
      [
        'paid',
        'Paid',
        'approved',
        'Approved',
        'active',
        'Active',
        'ACTIVE',
        'High',
        'Yes',
        'yes',
        'Completed',
        'Accepted',
        'Material received',
        'Sold',
        'Received Payment'
      ].includes(status)
    ) {
      return 'bg-[#3F8D78]';
    } else if (
      [
        'pending',
        'Pending',
        'Ongoing',
        'invite-sent',
        'Not Completed',
        'Material Dispatch',
        'Available'
      ].includes(status)
    ) {
      return 'bg-[#E97B37]';
    }
    return 'bg-gray-400';
  };

  if (step === 1) {
    return (
      <div className="flex flex-col gap-6">
        <div className="grid grid-cols-2 gap-4">
          <div
            className="p-4 border rounded-lg cursor-pointer hover:bg-gray-50 transition-all duration-200 flex flex-col items-center justify-center text-center"
            onClick={() => handlePricingTypeSelect('accommodation')}>
            <h3 className="text-base font-medium">Accommodation Based Extra Charge</h3>
            <p className="text-sm text-gray-600 mt-1">Create charges based on accommodation type</p>
          </div>
          {measurementTypeWithoutSlash === 'sqft' && (
            <div
              className="p-4 border rounded-lg cursor-pointer hover:bg-gray-50 transition-all duration-200 flex flex-col items-center justify-center text-center"
              onClick={() => handlePricingTypeSelect('sqft')}>
              <h3 className="text-base font-medium">sqft Based Extra Charge</h3>
              <p className="text-sm text-gray-600 mt-1">Create charges based on square feet</p>
            </div>
          )}
          {measurementTypeWithoutSlash === 'sqmt' && (
            <div
              className="p-4 border rounded-lg cursor-pointer hover:bg-gray-50 transition-all duration-200 flex flex-col items-center justify-center text-center"
              onClick={() => handlePricingTypeSelect('sqmt')}>
              <h3 className="text-base font-medium">sqmt Based Extra Charge</h3>
              <p className="text-sm text-gray-600 mt-1">Create charges based on square meters</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-6">
      <h3 className="text-lg font-medium mb-2">{getTypeTitle()}</h3>
      {extraCharges.map((_, index) => (
        <div key={index} className="flex flex-col gap-4 border p-4 rounded-lg">
          <label className="block text-sm font-medium">Pricing Type</label>
          <input type="text" disabled value={selectedPricingType} className="p-2 border rounded" />

          {/* <label className="block text-sm font-medium">Select Accommodation Type</label>
          <select
            value={selectedAccommodationType}
            onChange={handleAccommodationTypeChange}
            className="p-2 border rounded">
            <option value="GROUP">Group</option>
            <option value="INDEPENDENT">Independent</option>
            <option value="PARKING">Parking</option>
          </select> */}

          <label className="block text-sm font-medium">Select Accommodation</label>
          <div className="relative" data-dropdown>
            <div
              className="p-2 border rounded cursor-pointer flex items-center justify-between"
              onClick={() => toggleDropdown(index)}>
              <span
                className={
                  extraCharges[index].accommodation_ids.length > 0
                    ? 'text-gray-900'
                    : 'text-gray-500'
                }>
                {extraCharges[index].accommodation_ids.length > 0
                  ? `${extraCharges[index].accommodation_ids.length} accommodation(s) selected`
                  : 'Select Accommodation'}
              </span>
              <svg
                className={`w-4 h-4 transition-transform ${openDropdowns[index] ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>

            {openDropdowns[index] && (
              <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-y-auto">
                {accommodations.map((acc) => (
                  <label
                    key={acc.value}
                    className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={extraCharges[index].accommodation_ids.includes(acc.value)}
                      onChange={() => handleAccommodationSelection(index, acc.value)}
                      className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      onClick={(e) => e.stopPropagation()}
                    />
                    <span className="text-sm text-gray-700">{acc.label}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Display selected accommodations */}
          {extraCharges[index].accommodation_ids.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {extraCharges[index].accommodation_ids.map((selectedId) => {
                const selectedAcc = accommodations.find((acc) => acc.value === selectedId);
                return selectedAcc ? (
                  <div
                    key={selectedId}
                    className="flex items-center gap-2 px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                    <span>{selectedAcc.label}</span>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleAccommodationSelection(index, selectedId);
                      }}
                      className={`text-blue-600 hover:text-blue-800 hover:bg-blue-200 rounded-full w-5 h-5 flex items-center justify-center text-sm font-bold transition-all duration-200 ${
                        removingIds[index] === selectedId ? 'bg-red-200 text-red-600 scale-110' : ''
                      }`}
                      title="Remove accommodation"
                      disabled={removingIds[index] === selectedId}>
                      {removingIds[index] === selectedId ? '✓' : '×'}
                    </button>
                  </div>
                ) : null;
              })}
            </div>
          )}

          <label className="block text-sm font-medium">Extra Charge Name</label>
          <input
            type="text"
            value={extraCharges[index].name}
            onChange={(e) => handleInputChange(e, index, 'name')}
            className="p-2 border rounded"
            required
          />

          <label className="block text-sm font-medium">Description (Optional)</label>
          <input
            type="text"
            value={extraCharges[index].description}
            onChange={(e) => handleInputChange(e, index, 'description')}
            className="p-2 border rounded"
          />

          <label className="block text-sm font-medium">
            {`Price ${selectedPricingType ? `(${selectedPricingType})` : ''}`}
          </label>

          <input
            type="text"
            value={extraCharges[index].price}
            onChange={(e) => handleInputChange(e, index, 'price')}
            className="p-2 border rounded"
            required
            onInput={(e) => {
              let value = e.target.value;
              // Remove all non-numeric characters except dots
              value = value.replace(/[^0-9.]/g, '');
              // Ensure only one decimal point
              const parts = value.split('.');
              if (parts.length > 2) {
                value = parts[0] + '.' + parts.slice(1).join('');
              }
              e.target.value = value;
            }}
          />

          <div className="flex justify-end align-center gap-2">
            {extraCharges.length > 1 && (
              <Button
                type="button"
                onClick={() => removeExtraCharge(index)}
                displayType="primary-red">
                Delete
              </Button>
            )}

            {index === extraCharges.length - 1 && (
              <Button type="button" onClick={addExtraCharge} displayType="">
                Add More
              </Button>
            )}
          </div>
        </div>
      ))}

      <div className="flex justify-end items-center space-x-4 mt-4">
        <Button type="button" onClick={onClose} displayType="secondary">
          Cancel
        </Button>
        <Button type="submit">Create</Button>
      </div>
    </form>
  );
};
